// src/pages/Contact.tsx
import { useRef, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import Recaptcha, { RECAPTCHA_SITE_KEY, RecaptchaHandle } from '../components/ui/Recaptcha';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import SendRounded from '@mui/icons-material/SendRounded';
import HelpOutlineRounded from '@mui/icons-material/HelpOutlineRounded';
import FeedbackRounded from '@mui/icons-material/FeedbackRounded';
import HandshakeRounded from '@mui/icons-material/HandshakeRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import PersonRounded from '@mui/icons-material/PersonRounded';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import SubjectRounded from '@mui/icons-material/SubjectRounded';

const float1 = keyframes`0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(-30px,24px) scale(1.12)}`;
const float2 = keyframes`0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(36px,-20px) scale(.92)}`;

const TOPICS = [
  {
    key: 'Question',
    title: 'Questions',
    text: 'Need a hand with something? Ask away.',
    icon: HelpOutlineRounded,
    c: ['#2563EB', '#60A5FA'] as [string, string],
  },
  {
    key: 'Feedback',
    title: 'Feedback',
    text: 'Tell us what you love and what to improve.',
    icon: FeedbackRounded,
    c: ['#FF9800', '#E65100'] as [string, string],
  },
  {
    key: 'Partnership',
    title: 'Partnerships',
    text: 'Have an idea to build something together?',
    icon: HandshakeRounded,
    c: ['#11998E', '#38EF7D'] as [string, string],
  },
];

const FAQ = [
  { q: 'How do I sign in?', a: 'Use the Login button at the top of the page with your username (or email) and password. Tick "Remember me" to stay signed in on your own device.' },
  { q: 'Can I use QuillStack on my phone?', a: 'Yes. The same account works on the web and in the mobile app, and your notes, payments and lists stay in sync.' },
  { q: 'Is my data private?', a: 'Your data sits behind a protected, token-based sign-in, and each account only sees its own notes, payments and files.' },
  { q: 'What can I keep in QuillStack?', a: 'Notes, payments, grocery lists, documents, chats, tunes and videos, plus a password generator and an internet speed test.' },
];

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '16px', bgcolor: '#FAF7FF' },
  '& .MuiOutlinedInput-root fieldset': { borderWidth: 2, borderColor: 'rgba(98,38,167,.22)' },
  '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'rgba(98,38,167,.5)' },
  '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#6226A7' },
  '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: '0 0 0 5px rgba(98,38,167,.14)' },
} as const;

export default function Contact() {
  const [topic, setTopic] = useState('Question');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<{ name?: string; email?: string; message?: string }>({});

  const [captchaToken, setCaptchaToken] = useState('');
  const [sending, setSending] = useState(false);
  const captchaRef = useRef<RecaptchaHandle>(null);

  const submit = async () => {
    const next: typeof errors = {};
    if (!name.trim()) next.name = 'Please enter your name';
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) next.email = 'Enter a valid email address';
    if (!message.trim()) next.message = 'Please write a message';
    setErrors(next);
    if (Object.keys(next).length) return;

    if (RECAPTCHA_SITE_KEY && !captchaToken) {
      Swal.fire({ icon: 'warning', title: 'Captcha required', text: 'Please tick "I\'m not a robot" first.' });
      return;
    }

    setSending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BASE_URL}/contact`, {
        topic, name, email, subject, message, recaptchaToken: captchaToken || undefined,
      });
      Swal.fire({ icon: 'success', title: 'Message sent', text: 'Thanks! We will get back to you soon.' });
      setName(''); setEmail(''); setSubject(''); setMessage('');
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: 'Could not send', text: err?.response?.data?.message || 'Please try again.' });
    } finally {
      setSending(false);
      captchaRef.current?.reset();
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1536, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 2, md: 4 } }}>
      {/* ===== Hero ===== */}
      <Box
        className="qs-animate-in"
        sx={{
          position: 'relative', overflow: 'hidden', color: '#fff', borderRadius: '36px',
          p: { xs: 3.5, md: 6 }, textAlign: 'center',
          background: 'linear-gradient(120deg, #2E0F5E 0%, #6226A7 32%, #B13FD6 62%, #F6A938 100%)',
          backgroundSize: '220% 220%',
          boxShadow: '0 30px 60px rgba(98,38,167,.38), inset 0 0 0 1px rgba(255,255,255,.25)',
          '@media (prefers-reduced-motion: no-preference)': { animation: 'qsGradientShift 14s ease infinite' },
        }}
      >
        <Box sx={{ position: 'absolute', width: 380, height: 380, right: -90, top: -150, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,214,122,.6), transparent 65%)', '@media (prefers-reduced-motion: no-preference)': { animation: `${float1} 12s ease-in-out infinite` } }} />
        <Box sx={{ position: 'absolute', width: 420, height: 420, left: -130, bottom: -230, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,111,216,.5), transparent 65%)', '@media (prefers-reduced-motion: no-preference)': { animation: `${float2} 14s ease-in-out infinite` } }} />
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'linear-gradient(180deg, rgba(255,255,255,.22), rgba(255,255,255,0) 40%)' }} />

        <Box sx={{ position: 'relative', maxWidth: 920, mx: 'auto' }}>
          <Box
            sx={{
              display: 'inline-block', px: 1.75, py: 0.6, borderRadius: '999px', mb: 2, fontWeight: 800, fontSize: 13, letterSpacing: 0.4,
              background: 'rgba(255,255,255,.18)', border: '1px solid rgba(255,255,255,.35)', backdropFilter: 'blur(8px)',
            }}
          >
            ✉️ Contact us
          </Box>
          <Typography component="h1" sx={{ fontWeight: 900, fontSize: { xs: 32, md: 52 }, lineHeight: 1.08, letterSpacing: '-0.03em', textShadow: '0 6px 24px rgba(0,0,0,.25)' }}>
            We would love to{' '}
            <Box component="span" sx={{ background: 'linear-gradient(90deg,#FFE29A,#FFFFFF)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              hear from you.
            </Box>
          </Typography>
          <Typography sx={{ mt: 2, fontSize: { xs: 16, md: 19 }, opacity: 0.95, lineHeight: 1.6 }}>
            Questions, feedback, or partnership ideas? Send us a message and we will get back to you.
          </Typography>
        </Box>
      </Box>

      {/* ===== Topic cards ===== */}
      <Box sx={{ display: 'grid', gap: 2, mt: 3, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
        {TOPICS.map((t, i) => {
          const Icon = t.icon;
          const active = topic === t.key;
          return (
            <Box
              key={t.key}
              role="button"
              tabIndex={0}
              onClick={() => setTopic(t.key)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTopic(t.key); } }}
              className="qs-animate-in"
              sx={{
                position: 'relative', overflow: 'hidden', cursor: 'pointer', borderRadius: '24px', p: 2.25, minHeight: 130,
                display: 'flex', alignItems: 'center', gap: 2, outline: 'none',
                color: active ? '#fff' : 'text.primary',
                background: active ? `linear-gradient(135deg, ${t.c[0]}, ${t.c[1]})` : '#fff',
                border: '2px solid', borderColor: active ? 'transparent' : `${t.c[0]}33`,
                boxShadow: active ? `0 16px 32px ${t.c[1]}66, inset 0 0 0 1px rgba(255,255,255,.35)` : `0 10px 24px ${t.c[1]}22`,
                animationDelay: `${i * 0.06}s`,
                transition: 'transform .25s ease, box-shadow .25s ease',
                '&:hover': { transform: 'translateY(-5px)' },
                '&:focus-visible': { boxShadow: `0 0 0 3px #fff, 0 0 0 6px ${t.c[1]}` },
                '&::before': active ? {
                  content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
                  background: 'linear-gradient(135deg, rgba(255,255,255,.38), rgba(255,255,255,.06) 42%, rgba(255,255,255,0) 43%, rgba(0,0,0,.12))',
                } : undefined,
              }}
            >
              <Box
                sx={{
                  position: 'relative', width: 56, height: 56, borderRadius: '18px', display: 'grid', placeItems: 'center', flexShrink: 0,
                  color: '#fff',
                  background: active ? 'linear-gradient(135deg, rgba(255,255,255,.45), rgba(255,255,255,.12))' : `linear-gradient(135deg, ${t.c[0]}, ${t.c[1]})`,
                  border: active ? '1px solid rgba(255,255,255,.5)' : 'none',
                  boxShadow: active ? 'none' : `0 10px 20px ${t.c[1]}55, inset 0 1px 0 rgba(255,255,255,.45)`,
                }}
              >
                <Icon />
              </Box>
              <Box sx={{ position: 'relative' }}>
                <Typography sx={{ fontWeight: 900, fontSize: 19 }}>{t.title}</Typography>
                <Typography variant="body2" sx={{ opacity: active ? 0.95 : 0.75, lineHeight: 1.45 }}>{t.text}</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* ===== Form + FAQ ===== */}
      <Box sx={{ display: 'grid', gap: 3, mt: 3, gridTemplateColumns: { xs: '1fr', lg: '1.15fr 0.85fr' }, alignItems: 'start' }}>
        <Paper
          elevation={0}
          className="qs-animate-in"
          sx={{
            position: 'relative', overflow: 'hidden', borderRadius: '30px', p: { xs: 2.5, md: 4 },
            border: '1px solid rgba(98,38,167,.14)', boxShadow: '0 20px 44px rgba(98,38,167,.14)',
            background: 'linear-gradient(180deg,#fff,#FBF8FF)',
            '&::before': { content: '""', position: 'absolute', left: 0, right: 0, top: 0, height: 6, background: 'linear-gradient(90deg,#6226A7,#EC4899,#F6A938)' },
          }}
        >
          <Typography sx={{ fontWeight: 900, fontSize: 24 }}>Send a message</Typography>
          <Typography color="text.secondary" sx={{ mb: 2.5 }}>
            Topic: <Box component="span" sx={{ fontWeight: 800, color: '#6226A7' }}>{TOPICS.find((t) => t.key === topic)?.title}</Box>
          </Typography>

          <Stack spacing={2.25}>
            <Box sx={{ display: 'grid', gap: 2.25, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              <TextField
                label="Name"
                fullWidth
                value={name}
                onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: undefined })); }}
                error={!!errors.name}
                helperText={errors.name}
                sx={fieldSx}
                InputProps={{ startAdornment: <PersonRounded sx={{ color: '#6226A7', mr: 1 }} /> }}
              />
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: undefined })); }}
                error={!!errors.email}
                helperText={errors.email}
                sx={fieldSx}
                InputProps={{ startAdornment: <MailOutlineRounded sx={{ color: '#6226A7', mr: 1 }} /> }}
              />
            </Box>
            <TextField
              label="Subject"
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              sx={fieldSx}
              InputProps={{ startAdornment: <SubjectRounded sx={{ color: '#6226A7', mr: 1 }} /> }}
            />
            <TextField
              label="Message"
              fullWidth
              multiline
              minRows={6}
              value={message}
              onChange={(e) => { setMessage(e.target.value); setErrors((p) => ({ ...p, message: undefined })); }}
              error={!!errors.message}
              helperText={errors.message}
              sx={fieldSx}
            />
            <Recaptcha ref={captchaRef} onChange={setCaptchaToken} />
            <Button
              variant="contained"
              size="large"
              endIcon={<SendRounded />}
              disabled={sending}
              onClick={() => { void submit(); }}
              sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, px: 4.5, py: 1.4, borderRadius: '999px', fontWeight: 900, fontSize: 16 }}
            >
              {sending ? 'Sending…' : 'Send message'}
            </Button>
          </Stack>
        </Paper>

        <Box className="qs-animate-in" sx={{ animationDelay: '.08s' }}>
          <Typography sx={{ fontWeight: 900, fontSize: 24, mb: 1.5 }}>Quick answers</Typography>
          <Stack spacing={1.25}>
            {FAQ.map((f) => (
              <Accordion
                key={f.q}
                disableGutters
                elevation={0}
                sx={{
                  borderRadius: '20px !important', border: '1px solid rgba(98,38,167,.14)',
                  boxShadow: '0 8px 20px rgba(98,38,167,.08)', '&::before': { display: 'none' },
                  overflow: 'hidden',
                  '&.Mui-expanded': { borderColor: 'rgba(98,38,167,.4)', boxShadow: '0 14px 28px rgba(98,38,167,.16)' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreRounded sx={{ color: '#6226A7' }} />} sx={{ px: 2.25, py: 0.5 }}>
                  <Typography sx={{ fontWeight: 800 }}>{f.q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2.25, pb: 2, pt: 0 }}>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.6 }}>{f.a}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
